// ── CCC-866: THE ARC-CO / PLC ELECTION AND CONTRACT, READ OUT OF FSA'S OWN FORM ─────────────
//
// Kevin, 2026-10-27: "like with 156EZ, we need to be able to read and import this 866 report.
// This is exact payment info for 2026."
//
// One PDF bundles MANY FSA farms. Each farm is a 2-page CCC-866: page 1 is the form, page 2 is
// Privacy Act boilerplate. A farm with a second producer carries a "CONTINUATION OF OWNER'S OR
// PRODUCER'S COMMODITY INFORMATION" page between them (its own producer, its own payment shares).
// A blank page can sit anywhere. Per farm this reads the administrative state/county (name+code),
// the program year, the FSA farm number, PART A (commodity, ELECTED program, base acres, PLC
// yield), the multi-year contract box, PART B per producer (name, refusal boxes, payment share per
// commodity), the 16C signature date, the COC determination, and the footer date.
//
// ⚠ A PRODUCER'S SHARE IS NOT 100%, AND THAT IS NOT AN ERROR (Kevin, 2026-10-27). A landowner can
// rent one farm to two farmers; each holds a partial payment share, and the form a customer hands
// over may list only SOME producers. Shares are stored exactly as printed. A commodity whose listed
// shares total LESS than 100 is information ("other producers hold 19.5% — not on this form");
// only a total ABOVE 100 is a real inconsistency.
//
// ⚠ WHAT THE TEXT LAYER DOES NOT CARRY. The multi-year box, the refusal boxes and the COC
// approve/disapprove boxes are DRAWN paths, not text, and the signature date lives in a form
// field (a Tx widget), not in the content stream. src/ccc866Pdf.js reads those; flagsFromGeometry()
// below turns them into answers. Where a box cannot be read the answer is null = NOT KNOWN, never
// "no". The e-signature stamp's TIMESTAMP text sits inside a signature button's appearance and is
// not reachable by pdf.js — only the date widget and the button's presence are.
//
// Every figure is read by a deterministic parser (no model). A line the parser does not recognise
// is COUNTED and returned, never skipped.
//
// CommonJS, no browser APIs — App.jsx imports it and tools/check-ccc866.js requires it as-is.

const F = require("./fsa156ez");
const { clean, num, stateCode } = F;

const PARSER_VERSION = "ccc866-1";
const PROGRAM = { "ARC-COUNTY": "ARC-CO", "ARC-CO": "ARC-CO", PLC: "PLC", "ARC-INDIVIDUAL": "ARC-IC", "ARC-IC": "ARC-IC" };
const CROP_NAME = { SOYBEAN: "SOYBEANS" };
const cropName = (s) => { const k = clean(s).toUpperCase(); return CROP_NAME[k] || k; };
const cents = (v) => Math.round(Number(v) * 100);

function isoDate(s) {
  const m = String(s || "").match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  const n = String(s || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  return n ? n[0] : null;
}

// ── geometry → flags (used by the PDF reader, pure so the checker can drive it) ───────────
// items: [{ str, x, y, w }]; marks: small drawn paths [{ x0, y0, x1, y1 }]. A mark belongs to a
// label when it sits on the label's line and within a few points of an end of it. Measured on the
// real form: the multi-year box is drawn just RIGHT of its label; the refusal and COC boxes sit
// just LEFT of theirs.
function flagsFromGeometry(items, marks) {
  const its = (items || []).filter((i) => i && typeof i.str === "string");
  const mk = (marks || []).map((m) => ({ cx: (m.x0 + m.x1) / 2, cy: (m.y0 + m.y1) / 2 }));
  const find = (re) => its.filter((i) => re.test(i.str));
  const near = (label, side) => mk.some((m) => {
    if (Math.abs(m.cy - (label.y + 3)) > 8) return false;
    return side === "right" ? (m.cx >= label.x + label.w - 2 && m.cx <= label.x + label.w + 30) : (m.cx <= label.x + 4 && m.cx >= label.x - 26);
  });
  const out = { multi_year: null, coc: null, refused_arc: null, refused_plc: null };
  const my = find(/Multi-year Contract through/)[0];
  if (my) out.multi_year = near(my, "right");
  const ap = find(/^APPROVE$/)[0], dis = find(/^DISAPPROVE$/)[0];
  if (ap && dis) out.coc = near(ap, "left") ? "approve" : near(dis, "left") ? "disapprove" : null;
  const arc = find(/^All ARC-CO Payments/)[0], plc = find(/^All PLC Payments are/)[0];
  if (arc) out.refused_arc = near(arc, "left");
  if (plc) out.refused_plc = mk.some((m) => Math.abs(m.cy - (plc.y - 4)) <= 12 && m.cx <= plc.x + 4 && m.cx >= plc.x - 26);
  return out;
}

// ── the parser ───────────────────────────────────────────────────────────────────────────
const ENTRY_RE = /([A-Z][A-Z .&'\/-]*?)\s+(ARC-County|ARC-Individual|ARC-CO|ARC-IC|PLC)\s+(\d+(?:,\d{3})*(?:\.\d+)?)\s+(\d+(?:\.\d+)?)(?=\s|$)/g;
const SHARE_RE = /([A-Z][A-Z .&'\/-]*?)\s+(\d+(?:\.\d+)?)%/g;

const isBoiler = (lines) => lines.some((l) => /Privacy Act Statement/.test(l));
const pageOfN = (lines) => { for (const l of lines) { const m = l.match(/Page\s+(\d+)\s+of\s+(\d+)/i); if (m) return { page: +m[1], of: +m[2] }; } return null; };

// Reads Part A / Part B out of one form page (main or continuation).
function readBody(lines, page, unparsed) {
  const body = { commodities: [], producer_name: null, shares: [], multi_year_label: null, footer_date: null };
  let sect = null;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (/^PART A\b/.test(l)) { sect = "A"; continue; }
    if (/^PART B\b/.test(l)) { sect = "B"; continue; }
    if (/^PART C\b/.test(l)) { sect = "C"; continue; }
    let m;
    if ((m = l.match(/Multi-year Contract through (\d{4})/))) { body.multi_year_label = Number(m[1]); continue; }
    if ((m = l.match(/Original Employee Initials:.*?Date:\s*(\d{1,2}-\d{1,2}-\d{4})/))) { body.footer_date = isoDate(m[1]); continue; }
    if (sect === "A") {
      if (/^(6\.|Commodity\s+Program|Elected|No\s+Initials)/.test(l)) continue;
      ENTRY_RE.lastIndex = 0;
      let rest = l, any = false;
      while ((m = ENTRY_RE.exec(l))) {
        any = true;
        body.commodities.push({ commodity: cropName(m[1]), program: PROGRAM[m[2].toUpperCase()], base_acres: num(m[3]), plc_yield: num(m[4]) });
        rest = rest.replace(m[0], " ");
      }
      if (!any || clean(rest)) unparsed.push({ page, where: "part A", line: l });
    } else if (sect === "B") {
      if (/^12A\./.test(l)) { body.producer_name = clean(String(lines[i + 1] || "").split(/\s{3,}/)[0]) || null; i += 1; continue; }
      if (/^14\. Commodity/.test(l)) { sect = "shares"; continue; }
      // email / phone / address / refusal captions / signature captions: not stored, not an error
    } else if (sect === "shares") {
      if (/^16A\./.test(l)) { sect = "B2"; continue; }
      SHARE_RE.lastIndex = 0;
      let rest = l, any = false;
      while ((m = SHARE_RE.exec(l))) { any = true; body.shares.push({ commodity: cropName(m[1]), share: Number(m[2]) }); rest = rest.replace(m[0], " "); }
      if (!any || clean(rest)) unparsed.push({ page, where: "part B shares", line: l });
    }
  }
  return body;
}

// pages: [{ lines, flags?, widgets? }]  (flags from flagsFromGeometry; widgets = [{type,value}])
function parse866(pages) {
  const unparsed = [], skipped = { boilerplate: 0, blank: 0 }, farms = [], pageErrors = [];
  const byNumber = new Map();
  let last = null, year = null;
  (pages || []).forEach((pg, idx) => {
    const page = idx + 1, lines = (pg.lines || []).filter((l) => clean(l));
    if (!lines.length) { skipped.blank++; return; }
    if (isBoiler(lines)) {
      skipped.boilerplate++;
      if (last) { const p = pageOfN(lines); if (p) last.boiler_of = p.of; last.boiler_seen = true; }
      return;
    }
    const cont = lines.some((l) => /^CONTINUATION OF OWNER/.test(l));
    const isMain = !cont && lines.some((l) => /4\. Farm Number/.test(l));
    if (!cont && !isMain) { unparsed.push({ page, where: "page type", line: lines[0] }); return; }
    const flags = pg.flags || {};
    const sigWidget = (pg.widgets || []).find((w) => w.type === "Tx" && isoDate(w.value));
    const sigButton = (pg.widgets || []).some((w) => w.type === "Btn");
    const producerOf = (body, continuation) => ({
      name: body.producer_name, shares: body.shares, signed_date: sigWidget ? isoDate(sigWidget.value) : null, e_signed: sigButton,
      refused_arc: flags.refused_arc == null ? null : flags.refused_arc, refused_plc: flags.refused_plc == null ? null : flags.refused_plc,
      page, continuation, part_a: body.commodities,
    });
    if (isMain) {
      const hi = lines.findIndex((l) => /1\. Administrative State/.test(l));
      const hl = hi >= 0 ? lines.slice(hi + 1, hi + 4).find((l) => /^.+?\s{2,}\d+\s{2,}.+?\s{2,}\d+\s{2,}\d{4}\s*$/.test(l)) : null;
      const hm = hl && hl.match(/^(.+?)\s{2,}(\d+)\s{2,}(.+?)\s{2,}(\d+)\s{2,}(\d{4})\s*$/);
      const fl = lines.find((l) => /^\d{1,7}\s+\(\d{3}\)/.test(l));
      const farmNumber = fl ? fl.match(/^(\d+)/)[1] : null;
      if (!hm || !farmNumber) { unparsed.push({ page, where: "form header", line: hl || lines[0] }); return; }
      const body = readBody(lines, page, unparsed);
      const farm = {
        farm_number: farmNumber, state_name: clean(hm[1]), state_code: hm[2], admin_state: stateCode(hm[1]),
        county_name: clean(hm[3]), county_code: hm[4], program_year: Number(hm[5]),
        commodities: body.commodities, multi_year_label: body.multi_year_label,
        multi_year_through: flags.multi_year === true ? body.multi_year_label : null,
        multi_year_known: flags.multi_year === true || flags.multi_year === false,
        coc_determination: flags.coc || null, footer_date: body.footer_date,
        form_pages: [page], cont_pages: 0, boiler_of: null, boiler_seen: false,
        producers: [producerOf(body, false)],
      };
      if (byNumber.has(farmNumber)) pageErrors.push({ page, detail: `FSA farm ${farmNumber} appears on more than one form` });
      byNumber.set(farmNumber, farm); farms.push(farm); last = farm;
      if (year == null) year = farm.program_year;
    } else {
      const hi = lines.findIndex((l) => /1\. State Code/.test(l));
      const hl = hi >= 0 ? lines.slice(hi + 1, hi + 3).find((l) => /^\d+\s+\d+\s+\d{4}\s+\d+\s*$/.test(l)) : null;
      const hm = hl && hl.trim().split(/\s+/);
      const body = readBody(lines, page, unparsed);
      if (!hm) unparsed.push({ page, where: "continuation header", line: lines[0] });
      const target = hm ? (byNumber.get(hm[3]) || null) : last;
      if (!target) { pageErrors.push({ page, detail: "a continuation page names an FSA farm with no form in this file" }); return; }
      if (last && target !== last) pageErrors.push({ page, detail: `continuation for farm ${target.farm_number} does not follow its own form (it follows farm ${last.farm_number})` });
      target.cont_pages++; target.form_pages.push(page);
      target.producers.push(producerOf(body, true));
      last = target;
    }
  });
  return { parser_version: PARSER_VERSION, program_year: year, farms, skipped, unparsed, page_errors: pageErrors, page_count: (pages || []).length };
}

// ── identities ───────────────────────────────────────────────────────────────────────────
// FAILURES: a share total ABOVE 100%; a share for a commodity not in Part A; a continuation whose
// Part A differs from its form; page counts that do not add up; a missing producer name or share;
// unread lines. INFORMATION (never a failure): a total BELOW 100% — notes[].
function reconcile866(doc) {
  const failures = [], notes = [];
  const fail = (detail) => failures.push({ detail });
  for (const e of doc.page_errors || []) fail(`page ${e.page}: ${e.detail}`);
  const sig = (c) => `${c.commodity}|${c.program}|${cents(c.base_acres)}|${c.plc_yield}`;
  for (const f of doc.farms) {
    const tag = `FSA farm ${f.farm_number}`;
    if (!f.commodities.length) fail(`${tag}: no commodity rows were read from Part A`);
    const names = f.producers.map((p) => (p.name || "").toUpperCase()).filter(Boolean);
    if (new Set(names).size !== names.length) fail(`${tag}: the same producer name appears twice on the form — shares cannot be told apart`);
    const partA = new Set(f.commodities.map((c) => c.commodity));
    const aSig = f.commodities.map(sig).sort().join(";");
    f.producers.forEach((p) => {
      if (!p.name) fail(`${tag}: no producer name was read on page ${p.page}`);
      if (p.continuation && p.part_a.map(sig).sort().join(";") !== aSig) fail(`${tag}: page ${p.page} repeats Part A with different commodities, base acres or yields than the form`);
      for (const s of p.shares) if (!partA.has(s.commodity)) fail(`${tag}: ${p.name || "a producer"} holds a share of ${s.commodity}, which is not in Part A`);
    });
    for (const crop of partA) {
      const shares = f.producers.flatMap((p) => p.shares.filter((s) => s.commodity === crop).map((s) => cents(s.share)));
      const total = shares.reduce((a, b) => a + b, 0);
      if (!shares.length) fail(`${tag}: no payment share is printed for ${crop}`);
      else if (total > 10000) fail(`${tag}: payment shares for ${crop} total ${(total / 100).toFixed(2)}% — more than 100%`);
      else if (total < 10000) notes.push(`${tag} ${crop}: ${(total / 100).toFixed(2)}% is listed; the other ${((10000 - total) / 100).toFixed(2)}% is held by producers not on this form`);
    }
    if (f.boiler_seen && f.boiler_of != null && f.boiler_of !== 2 + f.cont_pages) fail(`${tag}: the form says it is ${f.boiler_of} pages but ${2 + f.cont_pages} were read`);
    if (f.program_year !== doc.program_year) fail(`${tag}: program year ${f.program_year} differs from ${doc.program_year}`);
  }
  if (!doc.farms.length) fail("no CCC-866 form was found in this file");
  if (doc.unparsed.length) fail(`${doc.unparsed.length} line(s) were not read`);
  return { ok: failures.length === 0, failures, notes };
}

// ── the customer's own share ─────────────────────────────────────────────────────────────
// `keys` = every normalised spelling the farmer answers to (App.jsx farmerNameKeys), `norm` =
// App.jsx normFarmerName. A producer matches only when its normalised name is one of the keys;
// no match is reported as no match — never a guess, never "the first producer".
function matchProducer(farm, keys, norm) {
  const set = new Set((keys || []).filter(Boolean));
  const hits = farm.producers.filter((p) => p.name && set.has(norm(p.name)));
  return hits.length === 1 ? hits[0] : null;
}

// A document is stored per FARMER + YEAR + ADMIN STATE CODE + ADMIN COUNTY CODE (sql/116): two FSA
// county offices give two CCC-866s, and re-uploading one county replaces only it. A form whose
// administrative state or county code cannot be read cannot be filed under any county.
function missingAdmin866(doc) {
  return (doc.farms || []).filter((f) => !f.state_code || !f.county_code).map((f) => f.farm_number);
}
function adminPairs866(doc) {
  const m = new Map();
  for (const f of doc.farms || []) if (f.state_code && f.county_code) m.set(f.state_code + "|" + f.county_code, { state_code: f.state_code, county_code: f.county_code, state_name: f.state_name, county_name: f.county_name });
  return [...m.values()].sort((a, b) => String(a.county_name).localeCompare(String(b.county_name)));
}
// the per-county upload rows of ONE program year, combined for the screen
function mergeUploads866(rows) {
  const r = (rows || []).slice().sort((a, b) => String(a.county_code).localeCompare(String(b.county_code)));
  if (!r.length) return null;
  return {
    program_year: r[0].program_year, file_name: [...new Set(r.map((x) => x.file_name).filter(Boolean))].join(", ") || null,
    storage_paths: [...new Set(r.map((x) => x.storage_path).filter(Boolean))],
    identity_ok: r.every((x) => x.identity_ok), identity_failures: [...new Set(r.flatMap((x) => x.identity_failures || []))],
    counties: r.map((x) => ({ state_code: x.state_code, county_code: x.county_code, file_name: x.file_name, form_count: x.form_count })),
  };
}

// ── persistence rows (ccc866_replace payload) ────────────────────────────────────────────
function rowsFor866(doc, { fileName } = {}) {
  const rc = reconcile866(doc);
  return {
    program_year: doc.program_year, file_name: fileName || null, parser_version: PARSER_VERSION,
    identity_ok: rc.ok, identity_failures: rc.failures.map((c) => c.detail), unparsed: doc.unparsed, unparsed_count: doc.unparsed.length,
    form_count: doc.farms.length,
    farms: doc.farms.map((f) => ({
      farm_number: f.farm_number, state_name: f.state_name, state_code: f.state_code, county_name: f.county_name, county_code: f.county_code,
      multi_year_through: f.multi_year_through, coc_determination: f.coc_determination, form_date: f.footer_date,
      commodities: f.commodities.map((c) => ({ commodity: c.commodity, program_elected: c.program, base_acres: c.base_acres, plc_yield: c.plc_yield })),
      producers: f.producers.map((p) => ({
        producer_name: p.name, signed_date: p.signed_date, e_signed: p.e_signed, refused_arc: p.refused_arc, refused_plc: p.refused_plc,
        continuation: p.continuation, shares: p.shares.map((s) => ({ commodity: s.commodity, payment_share: s.share })),
      })),
    })),
  };
}

// stored = { upload, elections:[ccc866_farm_elections rows], shares:[ccc866_producer_shares rows] }
// → per farm: elections by crop, producers with shares, the customer's own share and payment base.
function electionModel(stored, { keys = [], norm = (s) => String(s || "").toUpperCase() } = {}) {
  const { upload = {}, elections = [], shares = [] } = stored || {};
  const byFarm = new Map();
  const fk = (r) => `${r.state_code}|${r.county_code}|${r.farm_number}`;   // a farm number is unique only WITHIN a county
  for (const e of elections) {
    const f = byFarm.get(fk(e)) || byFarm.set(fk(e), { farm_number: e.farm_number, state_code: e.state_code, county_code: e.county_code, county_name: e.county_name, state_name: e.state_name,
      multi_year_through: e.multi_year_through, coc_determination: e.coc_determination, form_date: e.form_date, signed_date: e.signed_date, e_signed: e.e_signed, byCrop: {}, producers: [] }).get(fk(e));
    f.byCrop[e.commodity] = { program: e.program_elected, base_acres: e.base_acres, plc_yield: e.plc_yield };
    if (e.signed_date && !f.signed_date) f.signed_date = e.signed_date;
  }
  for (const s of shares) {
    const f = byFarm.get(fk(s)); if (!f) continue;
    let p = f.producers.find((x) => x.name === s.producer_name);
    if (!p) { p = { name: s.producer_name, shares: {}, refused_arc: s.refused_arc, refused_plc: s.refused_plc, signed_date: s.signed_date }; f.producers.push(p); }
    p.shares[s.commodity] = Number(s.payment_share);
  }
  const set = new Set((keys || []).filter(Boolean));
  const farms = [...byFarm.values()].sort((a, b) => String(a.state_name).localeCompare(String(b.state_name)) || String(a.county_name).localeCompare(String(b.county_name)) || Number(a.farm_number) - Number(b.farm_number)).map((f) => {
    const mine = f.producers.filter((p) => p.name && set.has(norm(p.name)));
    const me = mine.length === 1 ? mine[0] : null;
    const crops = {};
    for (const [crop, c] of Object.entries(f.byCrop)) {
      const listed = f.producers.reduce((a, p) => a + cents(p.shares[crop] || 0), 0) / 100;
      const myShare = me && me.shares[crop] != null ? me.shares[crop] : null;
      crops[crop] = { ...c, listed_share: listed, other_share: listed < 100 ? Math.round((100 - listed) * 100) / 100 : 0, over_100: listed > 100,
        my_share: myShare, my_base_acres: myShare != null && c.base_acres != null ? Math.round(c.base_acres * myShare) / 100 : null };
    }
    return { ...f, crops, me, name_matched: !!me, name_ambiguous: mine.length > 1 };
  });
  return { program_year: upload.program_year, file_name: upload.file_name, identity_ok: upload.identity_ok, identity_failures: upload.identity_failures || [], counties: upload.counties || [], storage_paths: upload.storage_paths || [], farms };
}

// payload (as built by rowsFor866) → the stored shape, so preview and saved page share electionModel.
function storedFromPayload866(p) {
  const elections = [], shares = [];
  for (const f of p.farms) {
    const first = f.producers.find((x) => x.signed_date) || f.producers[0] || {};
    for (const c of f.commodities) elections.push({ ...c, farm_number: f.farm_number, state_code: f.state_code, county_code: f.county_code, state_name: f.state_name, county_name: f.county_name,
      multi_year_through: f.multi_year_through, coc_determination: f.coc_determination, form_date: f.form_date, signed_date: first.signed_date || null, e_signed: !!first.e_signed });
    for (const pr of f.producers) for (const s of pr.shares) shares.push({ farm_number: f.farm_number, state_code: f.state_code, county_code: f.county_code, producer_name: pr.producer_name, commodity: s.commodity,
      payment_share: s.payment_share, refused_arc: pr.refused_arc, refused_plc: pr.refused_plc, signed_date: pr.signed_date });
  }
  return { upload: { program_year: p.program_year, file_name: p.file_name, identity_ok: p.identity_ok, identity_failures: p.identity_failures }, elections, shares };
}

// ── 156EZ vs 866 ─────────────────────────────────────────────────────────────────────────
// model156 = FSA156.baseAcreModel(...). One line per disagreement. The signed election outranks
// the 156EZ's election-or-default text (see electionOverrides) and the screen says so.
function compareWith156ez(m866, model156) {
  const out = [];
  if (!m866 || !model156) return out;
  const key = F.adminKey;
  const by156 = new Map((model156.farms || []).map((f) => [key(f), f]));
  for (const f of m866.farms) {
    const g = by156.get(key(f));
    if (!g) { out.push({ farm: f.farm_number, kind: "farm", detail: `FSA farm ${f.farm_number} (${f.county_name}) is on the CCC-866 but not on the stored 156EZ` }); continue; }
    for (const [crop, c] of Object.entries(f.crops)) {
      const x = g.byCrop[crop];
      if (!x) { out.push({ farm: f.farm_number, crop, kind: "crop", detail: `Farm ${f.farm_number} ${crop}: on the CCC-866 (${c.base_acres} ac) but no base acres on the 156EZ` }); continue; }
      if (cents(x.base_acres) !== cents(c.base_acres)) out.push({ farm: f.farm_number, crop, kind: "base", detail: `Farm ${f.farm_number} ${crop} base acres: CCC-866 ${c.base_acres} vs 156EZ ${x.base_acres}` });
      if (Number(x.plc_yield) !== Number(c.plc_yield)) out.push({ farm: f.farm_number, crop, kind: "yield", detail: `Farm ${f.farm_number} ${crop} PLC yield: CCC-866 ${c.plc_yield} vs 156EZ ${x.plc_yield}` });
    }
    for (const crop of Object.keys(g.byCrop)) if (!f.crops[crop]) out.push({ farm: f.farm_number, crop, kind: "crop", detail: `Farm ${f.farm_number} ${crop}: base acres on the 156EZ but not on the CCC-866` });
  }
  return out;
}

// state|county|farm number (no leading zeros) → crop → { program, signed_date }; baseAcreModel applies it.
function electionOverrides(m866) {
  const o = {};
  if (!m866) return o;
  for (const f of m866.farms) {
    const k = F.adminKey(f); o[k] = {};
    for (const [crop, c] of Object.entries(f.crops)) o[k][crop] = { program: c.program, signed_date: f.signed_date };
  }
  return o;
}

// ── BASE ACRE SUMMARY ───────────────────────────────────────────────────────
// One row per crop x elected program, summed over every FSA farm in the model (all admin
// counties), plus the same per county. Two acreages, and they are NOT the same number:
//   base_acres  — the farm's base acres as printed, whoever is paid on them;
//   pay_acres   — base acres x the customer's own share, ONLY where the customer is named on
//                 the form. A farm where the customer is not named (or the name is ambiguous)
//                 adds NOTHING to pay_acres and its base acres are reported as unknown_share_acres
//                 instead — a share that was not read is not a zero share.
// Integer hundredths throughout, so a 40-farm sum does not drift.
function baseAcreSummary866(model) {
  const cents = (v) => Math.round(Number(v) * 100);
  const out = (m) => [...m.values()]
    .map(({ base_c, pay_c, unk_c, ...r }) => ({ ...r, base_acres: base_c / 100, pay_acres: pay_c / 100, unknown_share_acres: unk_c / 100 }))
    .sort((a, b) => String(a.crop).localeCompare(String(b.crop)) || String(a.program || "~").localeCompare(String(b.program || "~")));
  const all = new Map(), byCounty = new Map(), countyName = new Map();
  const add = (m, key, base) => { let r = m.get(key); if (!r) m.set(key, r = { ...base, farms: 0, base_c: 0, pay_c: 0, unk_c: 0, base_unread: 0 }); return r; };
  for (const f of (model && model.farms) || []) {
    const ck = `${f.state_code}|${f.county_code}`;
    countyName.set(ck, { state_code: f.state_code, county_code: f.county_code, county_name: f.county_name, state_name: f.state_name });
    if (!byCounty.has(ck)) byCounty.set(ck, new Map());
    for (const crop of Object.keys(f.crops || {})) {
      const c = f.crops[crop];
      const program = c.program || null;
      for (const r of [add(all, crop + "|" + program, { crop, program }), add(byCounty.get(ck), crop + "|" + program, { crop, program })]) {
        r.farms += 1;
        if (c.base_acres == null) { r.base_unread += 1; continue; }
        r.base_c += cents(c.base_acres);
        if (f.name_matched && c.my_base_acres != null) r.pay_c += cents(c.my_base_acres);
        else r.unk_c += cents(c.base_acres);
      }
    }
  }
  const counties = [...byCounty.entries()].map(([k, m]) => ({ ...countyName.get(k), rows: out(m) }))
    .sort((a, b) => String(a.state_name).localeCompare(String(b.state_name)) || String(a.county_name).localeCompare(String(b.county_name)));
  const rows = out(all);
  const tot = (key) => Math.round(rows.reduce((s2, r) => s2 + cents(r[key]), 0)) / 100;
  return { rows, counties, total: { base_acres: tot("base_acres"), pay_acres: tot("pay_acres"), unknown_share_acres: tot("unknown_share_acres") } };
}

module.exports = { baseAcreSummary866, missingAdmin866, adminPairs866, mergeUploads866, PARSER_VERSION, parse866, reconcile866, rowsFor866, flagsFromGeometry, matchProducer, electionModel, storedFromPayload866, compareWith156ez, electionOverrides, isoDate };
